// IE 2026 09 24 / ODOO-… (заказ Игоря: объяснение элементов по Alt) · U3: КАРТА ОБЪЯСНЕНИЙ ОБЛАСТИ «widget» — шапка виджета и всё, что в ней лежит:
// src/components/widgets/Widget.vue, components_core/AttrButton.vue, AttrMenu.vue, AttrTabPanel.vue, AttrGrid.vue (список настроек),
// мобильный профиль (widget.mobile.*), форма «⋯» (widget.form.*), панель настроек Settings | Variants (widget.settings.*).
// 106 ключей по spec-widget.json (тексты выверены по коду коммита origin/main-2024-12 — см. README-U3.md, раздел «Что исправлено против спеки»).
// Запись реестра — utils/uiHelp.js / handoff-files/ui-help-2026-09-24/README.md: (vars, ctx) => { title, what, why, click, affects, stored, setting, hotkey, states }.
// Строки — английские литералы в t() (правило i18n; словари uk/ru — i18n_U3.json); поле setting — техническое имя и НЕ переводится (как в menuPanel.js / topbarNav.js).
import { t } from '@/i18n'

// IE 2026 11 03 / ODOO-350449 (заказ Игоря через webscm #127293: uiHelp угла после p226): прежние объяснения угла и формы «⋯» — при fe.ux.widgetCornerMenuLite = false (тексты без изменений)
const widgetCornerLegacy = (v, ctx) => ({
    title: t("Widget commands (⋯)"),
    what: t("The dotted corner opens a form with commands for the widget as a whole: close, expand, full screen, docked / free, header / tabs / panel, size."),
    why: t("Keeps rarely used commands out of the header."),
    click: t("Hover or click the corner — the form opens (always, whether or not “Open menus on hover” is on in the user menu); it closes shortly after the mouse leaves the corner and the form."),   // IE 2026 10 25 / ODOO-345044 (заказ Игоря: ховер углов виджета всегда)
    affects: t("Nothing changes."),
    stored: t("Nothing is stored."),
    hotkey: t("Alt W, Alt F, Shift Alt F, Alt P, Alt 1…8 work without the form"),
})
const widgetFormLegacy = (v, ctx) => ({
    title: t("Widget commands form"),
    what: t("Rows: close · expand; docked / free · close others · full screen; copy link · new tab · clone; Header · Tabs · Panel · Float; size cubes; exact size in % and Free."),
    why: t("One place for how the widget sits and looks on the page."),
    click: t("Click a command; the form closes when the mouse leaves it."),
    affects: t("Size and position change the page layout; Header / Tabs / Panel / Float change the view of this widget."),
    stored: t("Layout — with the page (Commands → Page → Save); view switches — with the widget's Save."),
    hotkey: t("Alt W, Alt F, Shift Alt F, Alt P, Alt L, Alt 1…8"),
})

const widget = {
    "widget.header": (v, ctx) => ({
        title: t("Widget header"),
        what: t("The top bar of the widget: type badge, title, commands of this widget and the ≡ menu."),
        why: t("Everything you do with the widget as a whole starts here; keyboard shortcuts act on the active widget, which has a coloured frame."),
        click: t("Click — make the widget active. An inactive widget shows its commands when the mouse is over it."),
        affects: t("Only this widget."),
        stored: t("Nothing is stored."),
        hotkey: t("Alt+Tab — switch between widgets of the page"),
        states: { "active": t("Active: shortcuts (Alt …, Esc) act on this widget."), "inactive": t("Not active: click it to work with it.") },
    }),
    "widget.header.title": (v, ctx) => ({
        title: (v && v.full) ? t("Widget title") + ": {full}" : t("Widget title"),   // IE 2026 09 27 / ODOO-341407 (ревью ie-dm7 #47608): урезанный заголовок на вкладке визуализации — полный текст в шапке объяснения по Alt
        what: t("The name of this widget — of the setting it shows."),
        why: t("Tells widgets apart on the page; a click here also makes the widget active."),
        click: t("Double-click — rename: Enter applies, Esc cancels (the next Esc closes the widget)."),
        affects: t("A new name marks the widget as changed (yellow Save). In the Ukrainian or Russian interface you rename it for that language only; the English name stays."),
        stored: t("In the widget setting after Save — then everyone who opens this setting sees it."),
        hotkey: t("Esc — cancel renaming"),
        states: { "view": t("Click to activate, double-click to rename."), "editing": t("Renaming: Enter — apply, Esc — cancel; the globe opens translations.") },
    }),
    "widget.header.strip": (v, ctx) => ({
        title: t("Header drag area"),
        what: t("The empty part of the widget header."),
        why: t("Moves the widget and switches it between the page grid and a free position without menus."),
        click: t("Drag — move: a docked widget dropped on another one moves to its place (the others shift), a free widget moves anywhere. Double-click — docked ↔ free (near the start of the title — rename)."),
        affects: t("The layout of this page; other docked widgets shift."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
        hotkey: t("Alt P — docked ↔ free"),
        states: { "docked": t("Docked in the page grid."), "free": t("Free, over the page.") },
    }),
    "widget.header.badge": (v, ctx) => ({
        title: (v && v.full) ? t("Widget type and settings") + ": {full}" : t("Widget type and settings"),   // IE 2026 09 28 / ODOO-341407: текст бейджа уступил место заголовку на вкладке визуализации — полный текст в шапке объяснения по Alt
        what: t("Shows what kind of widget this is (report, list, record …); for a record opened from a list, [n] is its position in the list. For widget settings it opens the list of saved settings and variants."),
        why: t("One report can be kept in several settings; here you switch between them and save your own."),
        click: t("Click — open or close the «Widget settings» panel (Settings | Variants). For records, tasks and other non-widget items the badge only shows the type."),
        affects: t("Choosing another setting reloads this widget with it."),
        stored: t("Nothing is stored."),
        states: { "closed": t("Click — open the list of settings and variants."), "open": t("The settings panel is open: click again or × to close."), "static": t("Only the type of the record; no settings list here.") },
    }),
    // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p216 бейдж виду віджета — тиха іконка, плашка при наведенні)
    // IE 2026 11 03 / ODOO-350449 (заказ Игоря: p226 полегшене монохромне меню кута віджета; поправка «вместо А1»): тихая шапка (v.quiet — fe.ux.kindBadgeOpaque) — плашки по наведению нет,
    // имя сегмента и варианта — здесь, клик по иконке — панель настроек (v.click)
    "widget.header.kindIcon": (v, ctx) => ({
        title: (v && v.name) ? t("Widget type") + ": {name}" + ((v && v.variant) ? " · " + t("Variant") + ": {variant}" : "") : t("Widget type"),
        what: t("The icon of the widget type: grey — a list or a source, coloured — a report, a task, a form or a chart."),
        why: (v && v.quiet) ? t("A quiet header: only the icon; the type name is shown here, by Alt.") : t("A quiet header: the full type badge with the settings switch appears when the mouse is over the header."),
        click: (v && v.quiet) ? ((v && v.click) ? t("Click — the «Widget settings» panel: settings and variants.") : t("Nothing happens on click.")) : t("Point at the header — the badge with the type name appears in place of the icon; click it to open the «Widget settings» panel."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.widgetKindBadgeIcon",
    }),
    "widget.header.variantLabel": (v, ctx) => ({
        title: t("Variant open"),
        what: t("A variant of a setting is open: the badge shows the name of its main setting, the title shows the variant."),
        why: t("Variants are alternative versions of one setting; this shows which main setting they belong to."),
        click: t("Click — open the settings panel; the Variants tab lists all variants of this setting."),
        affects: t("Nothing changes."),
        stored: t("Variants are separate saved records under their main setting."),
        setting: "window.settingsVariants.set(false)",
    }),
    "widget.header.drilldown": (v, ctx) => ({
        title: t("Decoded cells"),
        what: t("This widget decodes (drills down) cells of another report; the label names those cells."),
        why: t("Shows which numbers of the source report are broken down here."),
        click: t("Works as the type badge (opens the settings panel)."),
        affects: t("When the selection in the source report changes, this widget may be refreshed with the new cells."),
        stored: t("Only while the widget is open."),
    }),
    "widget.header.cascade": (v, ctx) => ({
        title: t("Calculation cascade"),
        what: t("This record takes part in the calculation cascade — by its own flag or because its sources are recalculated by the cascade."),
        why: t("Tells you the data depend on automatic recalculation and whether they are up to date."),
        click: t("Click — open the «Calculation cascade» tab of this form (if it has one)."),
        affects: t("Nothing changes."),
        stored: t("Computed by the server; updates by itself."),
        states: { "on": t("White: takes part in the cascade, the data are up to date."), "dirty": t("Yellow-brown: some sources are not recalculated yet — the data may be stale.") },
    }),
    "widget.header.dsMv": (v, ctx) => ({
        title: t("Query mode: DS | MV"),
        what: t("How this data source is built: DS — a plain query run every time; MV — a materialized view: the Query is the view body, Selection reads from the stored view."),
        why: t("A materialized view reads heavy data faster, but it has to be refreshed."),
        click: t("Click DS or MV — switch the mode; the title prefix changes to DS:: or MV::."),
        affects: t("The record becomes changed (Save); other widgets use the new mode only after Save."),
        stored: t("In the data source record, after Save."),
        states: { "ds": t("DS: plain query."), "mv": t("MV: materialized view.") },
    }),
    "widget.header.multiplySel": (v, ctx) => ({
        title: t("Put into the selector"),
        what: t("Shows whether the value this widget stands for (a country, a warehouse …) is in the workspace selector."),
        why: t("Filter the other widgets of the page by this widget's value in one click."),
        click: t("Click — add or remove the value; Alt+click (or Ctrl+click) — keep only this value."),
        affects: t("The workspace chips change; widgets that use this chip read their data again."),
        stored: t("In the workspace chips: kept in this browser; saved on the server via page chips → Commands → Filters → Save."),
        states: { "on": t("The value is in the selector."), "off": t("The value is not in the selector.") },
    }),
    "widget.header.nav.prev": (v, ctx) => ({
        title: t("Previous record"),
        what: t("Steps to the previous record of the list this form was opened from (in a row window — to the previous row of the table)."),
        why: t("Look through records one by one without going back to the list."),
        click: t("Click — open the previous record in this same widget; the number in the type badge is its position in the list."),
        affects: t("If the current record has unsaved changes, you will be asked first."),
        stored: t("Nothing is stored."),
        hotkey: t("Alt ←"),
        states: { "available": t("Available."), "end": t("This is the first record of the list.") },
    }),
    "widget.header.nav.next": (v, ctx) => ({
        title: t("Next record"),
        what: t("Steps to the next record of the list this form was opened from (in a row window — to the next row of the table)."),
        why: t("Look through records one by one without going back to the list."),
        click: t("Click — open the next record in this same widget; the number in the type badge is its position in the list."),
        affects: t("If the current record has unsaved changes, you will be asked first."),
        stored: t("Nothing is stored."),
        hotkey: t("Alt →"),
        states: { "available": t("Available."), "end": t("This is the last record of the list.") },
    }),
    "widget.header.cow": (v, ctx) => ({
        title: t("Branch copy / working version"),
        what: t("You are in a What-if branch: this record is the branch's own copy (changes stay in the branch) or the working version shown read-only."),
        why: t("Branch changes do not touch the working data until you apply them."),
        click: t("Hover — dates, authors, differences and actions: open the working version, all versions, apply to the working version, remove the branch copy."),
        affects: t("«Apply to working version» makes the changes visible outside the branch; «Remove branch copy» returns to the working version."),
        stored: t("Branch copies are separate records of the branch in the database."),
        setting: "fe.ux.hoverHideMs",
        states: { "copy": t("Branch copy: Save writes into the branch."), "working": t("Working version, read-only: the branch has its own copy.") },
    }),
    // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: подсветка изменений записи из аудита)
    "widget.header.auditDiff": (v, ctx) => ({
        title: t("Changes of this version"),
        what: t("This form shows a version of the record from the change log. The badge counts what differs from the next version (what this event changed) or from the previous one."),
        why: t("See at once which fields and table rows changed, without comparing by eye."),
        click: t("Hover — the other version, the summary, ‹ › to step through the changes; in Full comparison — the list of every change, click a line to go to it."),
        affects: t("Nothing changes in the record."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.auditRecordDiff",
        states: { "ok": t("Changed fields and cells are marked with an indigo corner."), "none": t("No differences."), "first": t("There is no other version to compare with."), "unavailable": t("The other version cannot be read; nothing is marked."), "pending": t("The comparison is not calculated yet or is being calculated.") },
    }),
    "widget.header.auditDiff.menu": (v, ctx) => ({
        title: t("Compare versions"),
        what: t("How the differences are shown and which version this one is compared with."),
        why: t("Look only at what changed, or at everything with the change list."),
        click: t("Mark changes — all fields and rows, changed ones get badges; Differences only — only changed fields and rows; Full comparison — badges plus the list of every change. Compare with — the next version (default: what this event changed) or the previous one."),
        affects: t("Only this form."),
        stored: t("The display mode is remembered in this browser."),
        setting: "fe.ux.auditRecordDiff",
        states: { "badges": t("Mark changes."), "diff": t("Differences only."), "full": t("Full comparison.") },
    }),
    "widget.header.auditDiff.nav": (v, ctx) => ({
        title: t("Go to change"),
        what: t("Steps to the previous or next changed field or table cell."),
        why: t("Walk through the changes one by one."),
        click: t("Click — select the change; its tab opens if needed."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.auditRecordDiff · fe.ux.compareMerge.linkRows",   // IE 2026 10 31 / ODOO-349799 (PR3): строка вкладки «Changes» ↔ ячейка формы
    }),
    "widget.header.auditDiff.calc": (v, ctx) => ({
        title: t("Calculate"),
        what: t("Compares this version with the chosen neighbouring version on the server; it was not calculated when the form opened to keep opening fast."),
        why: t("The comparison appears without reopening the form."),
        click: t("Click — calculate; the badge shows … until the answer comes."),
        affects: t("Nothing changes in the record."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.auditDiff.open": (v, ctx) => ({
        title: t("Open the other version"),
        what: t("Opens the version this one is compared with in a separate read-only widget."),
        why: t("See both versions side by side."),
        click: t("Click — open it next to this one."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.cow.openWorking": (v, ctx) => ({
        title: t("Open working version"),
        what: t("Opens the working version of this record (outside the branch) in a separate read-only widget."),
        why: t("Compare the branch copy with the working data."),
        click: t("Click — open it next to this one."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.cow.versions": (v, ctx) => ({
        title: t("Record versions"),
        what: t("Lists every version of this record: the working version and the copies in What-if branches."),
        why: t("See where the record was changed."),
        click: t("Click — open the list; open a row to see the record in that version."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.cow.apply": (v, ctx) => ({
        title: t("Apply to working version"),
        what: t("Writes the branch changes of this record into the working version and removes the branch copy."),
        why: t("Adopt the result of the branch for everyone."),
        click: t("Click — confirm, and the changes go to the working version."),
        affects: t("The changes become visible outside the branch, to everyone."),
        stored: t("In the database, immediately."),
    }),
    "widget.header.cow.remove": (v, ctx) => ({
        title: t("Remove branch copy"),
        what: t("Deletes the branch copy of this record; the working version is shown again."),
        why: t("Undo the branch changes of this record."),
        click: t("Click — confirm the removal."),
        affects: t("The branch changes of this record are lost."),
        stored: t("In the database, immediately."),
    }),
    "widget.header.reference": (v, ctx) => ({
        title: t("Reference view"),
        what: t("The widget shows the saved reference result of a data test, not live data."),
        why: t("Check what the test expects to get."),
        click: t("Hover — the test, when the reference was saved, its size and the chips it was taken with."),
        affects: t("Nothing changes."),
        stored: t("The reference is kept in the data test record."),
    }),
    "widget.header.reference.open": (v, ctx) => ({
        title: t("Open data test"),
        what: t("Opens the data test record this reference belongs to."),
        why: t("See or run the test."),
        click: t("Click — open it as a separate widget."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.auditIcon": (v, ctx) => ({
        title: t("Audit restore"),
        what: t("The widget shows an older version of the record taken from the audit log."),
        why: t("Bring a record back to an earlier state."),
        click: t("Save («Restore …») writes this old version back as the current one."),
        affects: t("After Restore everyone sees the old version."),
        stored: t("In the database, after Restore."),
    }),
    "widget.header.globe": (v, ctx) => ({
        title: t("Translations"),
        what: t("Names of this widget in every platform language."),
        why: t("Each user sees the name in the language of their interface."),
        click: t("Click — open the translations card; the text you typed goes in as a draft for the current language, and renaming in the header is cancelled."),
        affects: t("Nothing changes until you press Apply in the card."),
        stored: t("In the widget setting, per language, after Apply."),
        hotkey: t("Alt T — Translations… in the widget menu"),
    }),
    "widget.header.archived": (v, ctx) => ({
        title: t("Archived shown"),
        what: t("Archived records are included in this widget."),
        why: t("Warns that archived (deleted) items are shown too."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.activity": (v, ctx) => ({
        title: t("Paste progress"),
        what: t("Number of cells processed while pasting into this sheet."),
        why: t("Shows that a long paste is still running."),
        affects: t("The widget is busy until the paste ends."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.stop": (v, ctx) => ({
        title: t("Stop loading"),
        what: t("Stops loading data into this widget."),
        why: t("A heavy report can take long; stop it to change chips or settings first."),
        click: t("Click — cancel the current request; the server is asked to stop the query too."),
        affects: t("The widget keeps what it already had (it may stay empty); Update data loads again."),
        stored: t("Nothing is stored."),
        hotkey: t("Alt Enter — Update data"),
    }),
    "widget.header.btn.save": (v, ctx) => ({
        title: t("Save"),
        what: t("Writes the changes of this widget (setting, title, view, corrections) to the database."),
        why: t("Until you save, the changes live only in this browser tab."),
        click: t("Click — save. Under a What-if branch the changes go into the branch; the working version stays untouched."),
        affects: t("Everyone who opens this setting or record gets the saved version; dependent data may be recalculated."),
        stored: t("In the database, immediately."),
        hotkey: t("Alt S"),
    }),
    "widget.header.btn.update": (v, ctx) => ({
        title: t("Update data"),
        what: t("Reads the data of this widget again with the current chips and parameters."),
        why: t("The data may have changed since the widget was opened (recalculation, other users)."),
        click: t("Click — refresh. If the table has unsaved corrections, you will be asked first."),
        affects: t("Only this widget; its setting does not change."),
        stored: t("Nothing is stored."),
        hotkey: t("Alt Enter"),
    }),
    "widget.header.btn.hideEmpty": (v, ctx) => ({
        title: t("Hide empty rows, columns, tabs"),
        what: t("Hides rows, columns and tabs without data; the number on the button is how many are hidden. With the report option «Always show all columns» on, empty columns stay visible: only rows and tabs are hidden."),   // IE 2026 09 30 / ODOO-5800: опция отчёта show_all_columns
        why: t("Keeps large reports readable."),
        click: t("Click — switch hiding on or off."),
        affects: t("Only this widget. Switching it off also shows a hidden header, tab strip and command panel."),
        stored: t("Only while the widget is open; it opens with hiding on (a new record — off)."),
        hotkey: t("Alt A"),
        states: { "on": t("On: empty rows, columns and tabs are hidden (the number shows how many)."), "off": t("Off: everything is shown, including empty rows, columns and tabs.") },
    }),
    "widget.header.btn.mapping": (v, ctx) => ({
        title: t("Field mapping"),
        what: t("Shows how the fields of this widget are mapped by the data transformation task open on this page; the number is how many fields are mapped."),
        why: t("Check the source-to-target mapping visually."),
        click: t("Click — show or hide the lines connecting mapped fields with the task."),
        affects: t("Only the display on this page."),
        stored: t("Only while the widget is open."),
        states: { "on": t("Lines to the task are shown."), "off": t("Lines to the task are hidden.") },
    }),
    "widget.header.btn.docs": (v, ctx) => ({
        title: t("Documentation"),
        what: t("Opens the documentation for this kind of widget."),
        why: t("Explains fields and settings right where you use them."),
        click: t("Click — open the documentation at the section for the tab you are on."),
        affects: t("Opens a separate documentation widget."),
        stored: t("Nothing is stored."),
    }),
    // IE 2026 11 03 / ODOO-350881 (инвентаризация помощи, этап 1): «?» карточки помощи окна (utils/helpCard.js, флаг fe.ux.helpCard)
    "widget.header.btn.help": (v, ctx) => ({
        title: t("Help"),
        what: t("Opens the help card of this window: what it is for, what you can do or check in it, what kind of window it is and links to the user guide."),
        why: t("To understand a window without opening its settings."),
        click: t("Click — open or close the card. A link in the card opens the guide page in a documentation window."),
        affects: t("Nothing changes in the data."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.helpCard",
    }),
    "widget.header.btn.compare": (v, ctx) => ({
        title: t("Compare"),
        what: t("Compares this report with the previous process model (or with the saved reference of a data test)."),
        why: t("Shows what changed after a recalculation or a model switch."),
        click: t("Hover and choose: Mark changes — the report as is, changed cells get badges; Differences only — only changed rows with Δ; Full comparison — all rows with previous values, Δ and Δ%."),
        affects: t("Only this widget; the data are read again."),
        stored: t("Nothing is stored."),
    }),
    "widget.header.btn.optionGroup": (v, ctx) => ({
        title: t("Optional row group"),
        what: t("Turns an optional group of report rows (for example PO or KAM) on or off."),
        why: t("Hide what you don't need right now and keep the report lighter."),
        click: t("Click — show or hide the group. Rows and columns already loaded switch instantly; a group that was never built is built by the server."),   // IE 2026 10 22 / ODOO-5800: колонки — тоже на фронте
        affects: t("Only this widget."),
        stored: t("In this widget's setting; written to the database with the next Save."),
        setting: "fe.ux.optionGroupsFast; fe.ux.optionGroupColumnsFast; fe.ux.optionGroupColumnsReload",   // IE 2026 10 22 / ODOO-5800
        states: { "on": t("On: the rows of the group are shown."), "off": t("Off: the rows of the group are hidden or not built.") },
    }),
    // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p217 кнопка Σ — річні підсумки за опцією, типово вимкнено)
    "widget.header.btn.yearTotals": (v, ctx) => ({
        title: t("Year totals"),
        what: t("Year totals: show the total columns of each year"),
        why: t("By default the year total does not cut into the row of months; turn it on when you need the totals of the years."),
        click: t("Click — show or hide the total columns of all years at once. A collapsed year always shows its total."),
        affects: t("Only this widget; nothing is read again."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.yearTotalsButton; fe.ux.yearTotalsMuted; fe.ux.sigmaSteadyPanel",   // IE 2026 11 03 / ODOO-350449 (заказ Игоря: p233 Σ не смикає панель команд): + Σ по всем гридам виджета · IE 2026 11 02 / ODOO-350449 (дополнение Игоря к p217, вариант 1): + вид итога развёрнутого года
        states: { "on": t("On: the total columns of the years are shown."), "off": t("Off: the total columns of the years are hidden.") },
    }),
    // IE 2026 11 01 / ODOO-350449 (заказ Игоря: режими відображення комірок p149, T1): кнопка вида ячеек отчёта и выключатели режимов
    "widget.header.btn.cellView": (v, ctx) => ({
        title: t("Cell view"),
        what: t("Shows the date cells of the report as circles instead of plain numbers: statuses and stock zones, composition, stock impact or changes."),
        why: t("Patterns are easier to see than numbers: the circle area grows with the value, a coloured ring marks shortage, excess or growth."),
        click: t("Click — turn this view on; click the active view again — back to numbers. Only one view is active at a time. Hold Alt over a cell to see the layers and the number."),
        affects: t("Only this widget; nothing is recalculated."),
        stored: t("Nothing is stored."),
        // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p191 кола — за натиснутим видом у смузі, без опції «Кругові за статусами»): + fe.ux.cellViewsGateByView, fe.ux.optionGroupSegments
        setting: "fe.ux.cellViews; fe.ux.cellViewRing; fe.ux.cellView.<mode> (composition, status_zones, stock_impact, delta_prev_run, delta_30d); fe.ux.cellStatusPie; fe.ux.cellViewsDeferred; fe.ux.cellViewBars; fe.ux.cellViewArea; fe.ux.cellViewsGateByView; fe.ux.optionGroupSegments",   // IE 2026 11 01 / ODOO-350449 (заказ Игоря: режими відображення комірок p149, T2): + отложенные данные видов; IE 2026 11 01 / ODOO-350449 (заказ Игоря: форма Стовпчики та Запас площею — T15): + флаги форм
        states: { "on": t("On: date cells show circles."), "off": t("Off: date cells show numbers.") },
    }),
    "widget.header.btn.approve": (v, ctx) => ({
        title: t("Approve plans"),
        what: t("Approval commands for the rows of this report."),
        why: t("Fix the planned values as approved documents."),
        click: t("Hover and choose: approve the rows of the current selection, re-approve unchanged rows of another version, or cancel the latest approvals."),
        affects: t("Creates or cancels approval documents that other reports use."),
        stored: t("In the database, immediately."),
    }),
    "widget.header.btn.other": (v, ctx) => ({
        title: t("Widget command"),
        what: t("A command of this kind of widget (for example Choice, Open source segment, START)."),
        why: t("Frequent actions of the widget are at hand in its header."),
        click: t("Click — run it; the name and the shortcut are listed below."),
        affects: t("Depends on the command."),
        stored: t("Depends on the command."),
    }),
    "widget.header.tabsZone": (v, ctx) => ({
        title: t("Hidden tabs"),
        what: t("An invisible band on the lower edge of the header: the tabs of this widget are hidden."),
        why: t("Reach a tab without showing the tab strip again."),
        click: t("Hold the mouse on the band for about two seconds — a row of tabs appears; click a tab to switch."),
        affects: t("Only which tab of this widget is open."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.tabsHoverZoneMs",
    }),
    "widget.header.tabsPop": (v, ctx) => ({
        title: t("Tabs (strip hidden)"),
        what: t("The tabs of this widget while the tab strip is hidden; the number is the rows on that tab."),
        why: t("Switch tabs without changing the view settings."),
        click: t("Click a tab — open it; the row disappears when the mouse leaves it."),
        affects: t("Only which tab is open."),
        stored: t("Nothing is stored."),
    }),
    "widget.notification": (v, ctx) => ({
        title: t("Widget message"),
        what: t("The result or the error of the last action of this widget."),
        why: t("Tells you what happened without a separate window."),
        click: t("Hover — keep it on the screen; × — close."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
        states: { "success": t("Done: disappears by itself in about 8 seconds."), "warning": t("Warning: disappears in about 12 seconds."), "error": t("Error: stays until you close it."), "critical": t("Critical error: stays until you close it."), "pending": t("Waiting for the server.") },
    }),
    "widget.loading": (v, ctx) => ({
        title: t("Loading"),
        what: t("The moving line at the top: the widget is loading data; while exporting it grows with the percentage."),
        why: t("Shows that the widget is busy."),
        affects: t("Some commands wait until loading ends; «Stop loading» cancels it."),
        stored: t("Nothing is stored."),
    }),
    "widget.unsavedAsk": (v, ctx) => ({
        title: t("Unsaved changes"),
        what: t("Updating or reloading would drop what you changed and did not save."),
        why: t("Protects your corrections and settings from being lost."),
        click: t("Save & refresh — save, then refresh; Refresh without saving — drop the changes; Keep editing — cancel the refresh."),
        affects: t("Only this widget."),
        stored: t("Save & refresh writes to the database."),
    }),
    "widget.resize.dock": (v, ctx) => ({
        title: t("Resize"),
        what: t("The right and bottom edges and the bottom-right corner of a docked widget."),
        why: t("Fit the widget exactly between its neighbours."),
        click: t("Drag an edge — change the width or height (in % of the page); the page grid reflows."),
        affects: t("Neighbouring widgets move."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
        hotkey: t("Alt 1…8 — size presets"),
    }),
    "widget.resize.free": (v, ctx) => ({
        title: t("Resize (free widget)"),
        what: t("The edges and corners of a free widget."),
        why: t("Size a floating widget to the task."),
        click: t("Drag an edge or a corner — resize in that direction."),
        affects: t("Only this widget; the widgets underneath do not move."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
    }),
    // IE 2026 11 03 / ODOO-350449 (заказ Игоря через webscm #127293: uiHelp угла после p226): облегчённый угол (fe.ux.widgetCornerMenuLite, по умолчанию ВКЛ) — один ряд частых команд, «⋯» и плашки по наведению нет;
    // v.lite === false (Widget.vue передаёт cornerLite) — прежнее объяснение формы «⋯» (widgetCornerLegacy / widgetFormLegacy)
    "widget.corner.topLeft": (v, ctx) => ((v && v.lite === false) ? widgetCornerLegacy(v, ctx) : {
        title: t("Widget commands"),
        what: t("The top-left corner of the widget: pointing at it shows one row of frequent commands — close, expand to the page, close others, minimize, copy link, open in a new tab, clone — and the «Settings and variants» button if the widget has settings or variants to choose from."),
        why: t("Frequent commands are one move away; all the other commands of the widget (reload, pop out / dock back, full screen, header / tabs / panel / float, size) are in the ≡ menu, section Layout."),
        click: t("Hover or click the corner — the row opens (always, whether or not “Open menus on hover” is on in the user menu); it closes shortly after the mouse leaves the corner and the row. A click on the widget type icon opens the «Widget settings» panel (Settings | Variants) instead; the type name is shown above the icon by Alt."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.widgetCornerMenuLite",
        hotkey: t("Alt W, Alt F, Alt M, Alt L, Shift Alt F, Alt P, Alt 1…8 work without the row"),
    }),
    "widget.corner.topRight": (v, ctx) => ({
        title: t("Show hidden parts"),
        what: t("The dotted top-right corner: the header, tab strip or command panel of this widget is hidden."),
        why: t("Reach hidden commands without changing the view settings."),
        click: t("Hover — the hidden parts appear and stay until you switch Header / Tabs / Panel or reopen the widget."),
        affects: t("Only the display; the view settings do not change."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.cornerIsland",
    }),
    "widget.corner.island": (v, ctx) => ({
        title: t("Header island"),
        what: t("With the header hidden, a small island at the top right: type, title, expand, show the header."),
        why: t("Identify the widget without showing the whole header."),
        click: t("Arrows — expand to the page; chevron — show the header."),
        affects: t("Only the display."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.cornerIsland",
        hotkey: t("Alt F"),
    }),
    // IE 2026 11 03 / ODOO-350449 (заказ Игоря через webscm #127293: uiHelp угла после p226): облегчённый угол — ряд частых команд; v.lite === false — прежняя форма «⋯»
    "widget.form": (v, ctx) => ((v && v.lite === false) ? widgetFormLegacy(v, ctx) : {
        title: t("Widget commands row"),
        what: t("One row of frequent commands: close · expand to the page · close others · minimize · copy link · open in a new tab · clone; at its end — «Settings and variants» (if the widget has them) and, with the header hidden, the type and title. ≡ › Layout › «Width / height, % of page…» adds the fields of width, height and position and Free under the row."),
        why: t("Frequent commands without opening a menu; reload, pop out / dock back, full screen, header / tabs / panel / float and size are in ≡ › Layout."),
        click: t("Click a command; the row closes when the mouse leaves it."),
        affects: t("Close, expand and minimize change the view of the page; clone opens a copy next to it; copy link and new tab change nothing here."),
        stored: t("Layout — with the page (Commands → Page → Save); a clone — after Save in the copy."),
        setting: "fe.ux.widgetCornerMenuLite",
        hotkey: t("Alt W, Alt F, Alt M, Alt L, Shift Alt F, Alt P, Alt 1…8"),
    }),
    // IE 2026 11 03 / ODOO-350449 (заказ Игоря: p226 полегшене монохромне меню кута віджета): кнопка «Налаштування й варіанти» в конце ряда угла (у виджета с выбором настроек / вариантов)
    "widget.form.variants": (v, ctx) => ({
        title: t("Settings and variants"),
        what: (v && v.variant) ? t("Opens the «Widget settings» panel: other settings of this widget and the variants of the current one. Open now: {name} · variant {variant}.") : t("Opens the «Widget settings» panel: other settings of this widget and the variants of the current one. Open now: {name}."),
        why: t("Switch the widget to another setting or variant without the header badge."),
        click: t("Click — the panel opens; click again or Esc — closes."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.widgetCornerMenuLite",
        states: { "open": t("The panel is open."), "closed": t("The panel is closed.") },
    }),
    "widget.form.reload": (v, ctx) => ({
        title: t("Reload (drop changes)"),
        what: t("Reopens the widget from the database; unsaved changes are dropped."),
        why: t("Go back to the saved state after unwanted edits."),
        click: t("Click — you will be asked: save first, drop the changes or keep editing."),
        affects: t("Only this widget."),
        stored: t("Nothing is stored."),
        hotkey: t("Shift Alt Enter — Reload in the widget menu"),
    }),
    "widget.form.close": (v, ctx) => ({
        title: t("Close widget"),
        what: t("Closes this widget on the page; nothing is deleted."),
        why: t("Free the space; the widget can be brought back."),
        click: t("Click — close. Alt+Esc returns the last closed widget; Alt+Tab lists the closed widgets of the page."),
        affects: t("Row windows opened from it close too; another widget becomes active. The saved set of widgets of the page changes only when you save the page."),
        stored: t("Only in this session."),
        hotkey: t("Alt W or Esc; Alt+Esc — reopen"),
    }),
    "widget.form.expand": (v, ctx) => ({
        title: t("Expand to the page"),
        what: t("Spreads this widget over the whole page area; the other widgets stay underneath."),
        why: t("Work with a big table or chart without changing the layout."),
        click: t("Click — expand; click again or press Esc — back to the normal size."),
        affects: t("Only the view in this tab; the page layout does not change."),
        stored: t("Not saved."),
        hotkey: t("Alt F; Esc — restore"),
        states: { "on": t("Expanded: Esc or Alt F restores the size."), "off": t("Normal size.") },
    }),
    "widget.form.ident": (v, ctx) => ({
        title: t("Type and title (header hidden)"),
        what: t("The header of this widget is hidden, so its type badge and title are shown here."),
        why: t("Identify the widget and reach its settings without showing the header."),
        click: t("Click the badge — the settings panel; double-click the title — rename."),
        affects: t("The same as in the header."),
        stored: t("Nothing is stored."),
        setting: "fe.ux.topLeftIdent",
    }),
    "widget.form.minimize": (v, ctx) => ({
        title: t("Minimize"),
        what: t("Collapses the widget into a small bar at the bottom left of the page."),
        why: t("Keep the widget at hand without taking space."),
        click: t("Alt M or a double-click on the bar — restore."),
        affects: t("The page layout reflows."),
        stored: t("Only in this session."),
        hotkey: t("Alt M"),
    }),
    "widget.form.popout": (v, ctx) => ({
        title: t("Pop out / dock back"),
        what: t("Switches the widget between the page grid (docked) and a free position over the page."),
        why: t("A free widget can stay on top while you work with the others."),
        click: t("Click — pop out or dock back."),
        affects: t("The page grid reflows. How free widgets behave over the active one — user menu → Widgets → Behavior."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
        setting: "fe.ux.freeWidgets",
        hotkey: t("Alt P"),
        states: { "docked": t("Docked in the page grid."), "free": t("Free, over the page.") },
    }),
    "widget.form.closeOthers": (v, ctx) => ({
        title: t("Close other widgets"),
        what: t("Closes all other widgets of this page; this one stays."),
        why: t("Focus on one widget quickly."),
        click: t("Click — close the others. Alt+Esc returns them one by one, the last closed first."),
        affects: t("Earlier closed ones can be returned for about five minutes; the last one — at any time in this session."),
        stored: t("Only in this session; the saved page changes only when you save it."),
        hotkey: t("Alt+Esc — reopen the last closed"),
    }),
    "widget.form.fullscreen": (v, ctx) => ({
        title: t("Full screen"),
        what: t("Shows only this widget on the whole screen (browser full screen)."),
        why: t("Presentations and very large tables."),
        click: t("Click — full screen; click again or press Esc — back."),
        affects: t("Only the view."),
        stored: t("Not saved."),
        hotkey: t("Shift Alt F"),
        states: { "on": t("Full screen: Esc leaves it."), "off": t("Normal view.") },
    }),
    "widget.form.copyLink": (v, ctx) => ({
        title: t("Copy link"),
        what: t("Copies a link that opens this page with the current workspace chips and this widget."),
        why: t("Send a colleague exactly what you see."),
        click: t("Click — the link goes to the clipboard (on a phone — the share sheet)."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.form.newTab": (v, ctx) => ({
        title: t("Open in a new tab"),
        what: t("Opens the same link — this page, the current chips and this widget — in a new browser tab."),
        why: t("Keep this view and continue in another tab."),
        click: t("Click — a new browser tab opens."),
        affects: t("Nothing changes here."),
        stored: t("Nothing is stored."),
    }),
    "widget.form.clone": (v, ctx) => ({
        title: t("Clone"),
        what: t("Opens a copy of this widget with a new, not yet saved setting."),
        why: t("Try changes without touching the original."),
        click: t("Click — the copy opens next to it with a numbered name; press Save in the copy to keep it."),
        affects: t("The original stays as it is. The saved copy is visible to others and appears in the settings tree under the original."),
        stored: t("Only after Save in the copy."),
        hotkey: t("Alt L"),
    }),
    "widget.form.closeChildren": (v, ctx) => ({
        title: t("Close child widgets"),
        what: t("Closes all widgets opened from this one (row windows, records, decodings); the number is how many."),
        why: t("Clean up the page after drilling down."),
        click: t("Click — close them."),
        affects: t("They close for good: Alt+Esc does not bring them back."),
        stored: t("Only in this session."),
    }),
    "widget.form.header": (v, ctx) => ({
        title: t("Header"),
        what: t("Shows or hides the header of this widget (type, title, commands, ≡ menu)."),
        why: t("More room for the content; hidden parts stay reachable."),
        click: t("Click — switch. With the header hidden, the type and title move into the top-left corner commands; hovering the dotted top-right corner shows the hidden parts again."),   // IE 2026 11 03 / ODOO-350449 (заказ Игоря через webscm #127293: uiHelp угла после p226): пункт есть и в форме «⋯», и в ≡ › Layout — «this form» неверно в меню
        affects: t("Only this widget."),
        stored: t("In the view settings of the widget; written to the database with the next Save of the widget (the switch itself does not mark it changed) — then for everyone who opens this setting."),
        states: { "on": t("Green: the header is shown."), "off": t("White: the header is hidden.") },
    }),
    "widget.form.tabs": (v, ctx) => ({
        title: t("Tabs"),
        what: t("Shows or hides the tab strip of this widget."),
        why: t("More room for the content of the open tab."),
        click: t("Click — switch. With the strip hidden, hold the mouse on the lower edge of the header (about 2 s) to pick a tab."),
        affects: t("Only this widget; with Float on and tabs hidden, the content fills the whole frame."),
        stored: t("In the view settings of the widget; written to the database with the next Save of the widget (the switch itself does not mark it changed) — then for everyone who opens this setting."),
        states: { "on": t("Green: the tab strip is shown."), "off": t("White: the tab strip is hidden.") },
    }),
    "widget.form.panel": (v, ctx) => ({
        title: t("Panel"),
        what: t("Shows or hides the command panel above the tables of this widget (chips and table commands)."),
        why: t("More room for rows when you don't need the table commands."),
        click: t("Click — switch."),
        affects: t("Only this widget; hidden chips still filter the table."),
        stored: t("In the view settings of the widget; written to the database with the next Save of the widget (the switch itself does not mark it changed) — then for everyone who opens this setting."),
        states: { "on": t("Green: the table command panel is shown."), "off": t("White: the table command panel is hidden.") },
    }),
    "widget.form.float": (v, ctx) => ({
        title: t("Float"),
        what: t("The title and the commands become floating islands over the content; with tabs hidden, the content (chart, map) fills the whole frame."),
        why: t("Maximum room for charts and maps while keeping the commands."),
        click: t("Click — switch floating islands on or off."),
        affects: t("Only this widget. A map without a tab strip floats by default."),
        stored: t("In the view settings of the widget; written to the database with the next Save of the widget (the switch itself does not mark it changed) — then for everyone who opens this setting."),
        states: { "on": t("Green: floating islands."), "off": t("White: an ordinary header row.") },
    }),
    "widget.form.scale": (v, ctx) => ({
        title: t("Size presets"),
        what: t("Eight cubes of a 4 × 2 grid: the column sets the width (a quarter of the page each), the row the height (half the page each)."),
        why: t("Quick standard sizes that tile the page neatly."),
        click: t("Click a cube — the widget takes that size; yellow cubes show the current size, blue ones the size under the mouse."),
        affects: t("Neighbouring widgets move."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
        hotkey: t("Alt 1…4 — half height, Alt 5…8 — full height"),
    }),
    "widget.form.width": (v, ctx) => ({
        title: t("Width, %"),
        what: t("Width of the widget in percent of the page."),
        why: t("Exact size and position when the cubes are too coarse."),
        click: t("Type 1–100 and press Enter or leave the field."),
        affects: t("The page grid reflows."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
    }),
    "widget.form.height": (v, ctx) => ({
        title: t("Height, %"),
        what: t("Height of the widget in percent of the page."),
        why: t("Exact size and position when the cubes are too coarse."),
        click: t("Type 1–100 and press Enter or leave the field."),
        affects: t("The page grid reflows."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
    }),
    "widget.form.left": (v, ctx) => ({
        title: t("Left, %"),
        what: t("Distance of a free widget from the left edge of the page, in percent."),
        why: t("Place a free widget exactly."),
        click: t("Type 0–100 and press Enter or leave the field."),
        affects: t("Only this widget."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
    }),
    "widget.form.top": (v, ctx) => ({
        title: t("Top, %"),
        what: t("Distance of a free widget from the top of the page, in percent."),
        why: t("Place a free widget exactly."),
        click: t("Type 0–100 and press Enter or leave the field."),
        affects: t("Only this widget."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
    }),
    "widget.form.free": (v, ctx) => ({
        title: t("Free"),
        what: t("Switches the widget between the page grid and a free position over the page, keeping the sizes typed in the fields."),
        why: t("Place the widget exactly by X / Y."),
        click: t("Click — free (green) or back into the grid (white)."),
        affects: t("The page grid reflows; free widgets over the active one behave as set in user menu → Widgets → Behavior."),
        stored: t("In the page layout for this session; to keep it, save the page: page chips → Commands → Page → Save (✓)."),
        setting: "fe.ux.freeWidgets",
        hotkey: t("Alt P"),
        states: { "on": t("Green: free position over the page (X / Y in %)."), "off": t("White: docked in the page grid.") },
    }),
    "widget.form.mobSettings": (v, ctx) => ({
        title: t("Widget settings (phone)"),
        what: t("Opens the list of settings and variants of this widget as a sheet."),
        why: t("On a phone there is no hover over the type badge."),
        click: t("Tap — open or close the sheet."),
        affects: t("Choosing a setting reloads the widget."),
        stored: t("Nothing is stored."),
    }),
    "widget.form.mobCommands": (v, ctx) => ({
        title: t("Header commands (phone)"),
        what: t("The commands of the widget header, repeated in this form on a phone."),
        why: t("The header row is too narrow on a phone."),
        click: t("Tap a command — run it."),
        affects: t("Depends on the command."),
        stored: t("Depends on the command."),
    }),
    "widget.settings.panel": (v, ctx) => ({
        title: t("Widget settings"),
        what: t("All saved settings of this widget (Settings) and the variants of the current one (Variants)."),
        why: t("One report can be kept in several configurations: pick one or save your own."),
        click: t("Click a name — open it in this widget. Drag the bottom-right corner to resize the panel; × or a click outside closes it."),
        affects: t("Choosing a setting reloads this widget."),
        stored: t("Nothing is stored."),
        states: { "settings": t("Showing the settings tree."), "variants": t("Showing the variants of the current setting.") },
    }),
    "widget.settings.close": (v, ctx) => ({
        title: t("Close the panel"),
        what: t("Closes the settings panel."),
        why: t("Back to the widget."),
        click: t("Click — close; a click outside the panel closes it too."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.settings.recordTitle": (v, ctx) => ({
        title: t("Open setting"),
        what: t("The name of the setting open in this widget now."),
        why: t("Shows what you are about to change or copy."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.settings.treeHeader": (v, ctx) => ({
        title: t("Settings tree"),
        what: t("Title of the list; in Settings mode it also opens all settings of this widget type as a full list."),
        why: t("A full list allows filtering, sorting and bulk actions on settings."),
        click: t("Click «Settings tree» — open all settings in a list widget (extended view rights needed)."),
        affects: t("Opens a separate list widget; the panel closes."),
        stored: t("Nothing is stored."),
        states: { "settings": t("Click the title — open all settings as a list."), "variants": t("Title only; no action.") },
    }),
    "widget.settings.mode.settings": (v, ctx) => ({
        title: t("Settings"),
        what: t("The tree of saved settings of this widget; the number is how many there are."),
        why: t("Switch to another configuration of the same widget."),
        click: t("Click — show the settings tree."),
        affects: t("Only the list in the panel."),
        stored: t("Your choice of Settings / Variants is remembered in this browser for this widget."),
        states: { "active": t("Shown now."), "inactive": t("Click to switch.") },
    }),
    "widget.settings.mode.variants": (v, ctx) => ({
        title: t("Variants"),
        what: t("Variants of the current setting — alternative versions kept under it, not in the main tree; the number is how many."),
        why: t("Keep personal or scenario versions of one setting without cluttering the tree."),
        click: t("Click — show the variants; the first row «(main)» is the setting itself."),
        affects: t("Only the list in the panel."),
        stored: t("Your choice of Settings / Variants is remembered in this browser for this widget."),
        setting: "window.settingsVariants.set(false)",
        states: { "active": t("Shown now."), "inactive": t("Click to switch.") },
    }),
    "widget.settings.list.title": (v, ctx) => ({
        title: t("Setting"),
        what: t("The name of a saved setting or variant."),
        why: t("Pick the configuration to show in this widget."),
        click: t("Click the name — open it in this widget (from Settings its default variant opens: yours first, then the one for all). Click the icon — open it as a separate widget."),
        affects: t("This widget reloads; if it has unsaved changes, you will be asked first."),
        stored: t("Nothing is saved; to keep the choice on the page, save the page (Commands → Page → Save)."),
    }),
    "widget.settings.list.current": (v, ctx) => ({
        title: t("Current"),
        what: t("Marks the setting open in this widget now; «Current · variant» — one of its variants is open."),
        why: t("Find where you are in the tree."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
        states: { "current": t("This setting is open."), "variant": t("A variant of this setting is open.") },
    }),
    "widget.settings.list.all": (v, ctx) => ({
        title: t("All — default for everyone"),
        what: t("Which setting or variant opens for everyone instead of the main setting."),
        why: t("Give all users a ready configuration without changing the main one."),
        click: t("Click a cell — make this row the default for all (only one); click the ticked one or the main row — clear it. Only the owner of the main setting or an admin can change it."),
        affects: t("Everyone who opens this widget gets it, unless they have their own default (Me)."),
        stored: t("In the main setting, in the database, immediately."),
    }),
    "widget.settings.list.me": (v, ctx) => ({
        title: t("Me — default for me"),
        what: t("Which setting or variant opens for you instead of the main setting."),
        why: t("Your own starting configuration without affecting others."),
        click: t("Click a cell — make this row your default; click it again or the main row — clear it."),
        affects: t("Only you; it wins over «All»."),
        stored: t("In your user record, in the database, immediately."),
    }),
    "widget.settings.list.rowHeader": (v, ctx) => ({
        title: t("Apply"),
        what: t("The ✓ in front of a row applies that setting."),
        why: t("A quick way to switch the widget."),
        click: t("Click — open this row in this widget (the same as clicking its name or double-clicking)."),
        affects: t("This widget reloads."),
        stored: t("Nothing is stored."),
    }),
    "widget.settings.saveAsNew": (v, ctx) => ({
        title: t("Save as new"),
        what: t("Saves the current state of the widget as a new setting (Settings) or as a new variant of this setting (Variants); the copy is yours and hidden from others."),
        why: t("Keep your configuration without changing the shared one."),
        click: t("Click — the copy is saved right away and opened in this widget with a numbered name; rename it by double-clicking the title."),
        affects: t("The original stays as it was; other users do not see your copy."),
        stored: t("In the database, immediately."),
        states: { "settings": t("Creates a new setting in the settings tree, under the main setting."), "variants": t("Creates a new variant of the current setting, under the selected variant.") },
    }),
    "widget.settings.variantsEmpty": (v, ctx) => ({
        title: t("No variants yet"),
        what: t("This setting has no variants yet."),
        why: t("Variants appear after the first «Save as new» in this mode."),
        click: t("«Save as new» below creates the first one."),
        affects: t("Nothing changes."),
        stored: t("Nothing is stored."),
    }),
    "widget.settings.cmd.apply": (v, ctx) => ({
        title: t("Apply the selected setting"),
        what: t("Opens the selected row in this widget."),
        why: t("Switch the configuration from the menu."),
        click: t("Select a row, then run the command (or just click the name)."),
        affects: t("This widget reloads."),
        stored: t("Nothing is stored."),
    }),
    "widget.settings.cmd.openNew": (v, ctx) => ({
        title: t("Open in a new widget"),
        what: t("Opens the selected setting (or variant) as a separate widget on the page; this one stays."),
        why: t("Compare two settings side by side."),
        click: t("Select a row, then run the command."),
        affects: t("Adds a widget to the page."),
        stored: t("Only in this session."),
    }),
    "widget.settings.cmd.archive": (v, ctx) => ({
        title: t("Archive"),
        what: t("Marks the selected setting (or variant) for deletion; its children move one level up."),
        why: t("Remove outdated settings without losing them."),
        click: t("Select a row, run the command and confirm."),
        affects: t("It disappears from the tree for everyone; the main setting itself is archived from the Settings tab."),
        stored: t("In the database, immediately."),
    }),
    "widget.settings.cmd.saveSetting": (v, ctx) => ({
        title: t("Save as new setting (shared)"),
        what: t("Saves the current state as a new setting in the main tree, under the main setting — visible to others."),
        why: t("Share a new configuration with colleagues."),
        click: t("Run — the copy is saved and opened in this widget."),
        affects: t("Others see it in the settings tree."),
        stored: t("In the database, immediately."),
    }),
    "widget.settings.cmd.saveVariant": (v, ctx) => ({
        title: t("Save as new variant (shared)"),
        what: t("Saves the current state as a new variant of this setting — visible to others."),
        why: t("Offer an alternative version of the setting to everyone."),
        click: t("Run — the variant is saved and opened in this widget."),
        affects: t("Others see it on the Variants tab."),
        stored: t("In the database, immediately."),
    }),
    "widget.settings.cmd.onlyMine": (v, ctx) => ({
        title: t("Only my settings"),
        what: t("Shows only the settings (variants) you created; run again — all of them."),
        why: t("Find your own configurations quickly."),
        click: t("Run — switch the filter."),
        affects: t("Only the list in the panel."),
        stored: t("Only while the widget is open."),
    }),
    "widget.settings.cmd.showHidden": (v, ctx) => ({
        title: t("Show hidden"),
        what: t("Also shows settings (variants) that their owners made private."),
        why: t("Find a colleague's private configuration."),
        click: t("Run — switch."),
        affects: t("Only the list in the panel."),
        stored: t("Only while the widget is open."),
    }),
    "widget.settings.cmd.resetView": (v, ctx) => ({
        title: t("Reset view"),
        what: t("Returns the list to its default look: a tree by parent, all settings, no tag filters."),
        why: t("Undo the filters of the list at once."),
        click: t("Run — the view is reset."),
        affects: t("Only the list in the panel."),
        stored: t("Only while the widget is open."),
    }),
    "widget.settings.cmd.openList": (v, ctx) => ({
        title: t("Open in list widget"),
        what: t("Opens the settings of this widget type as a list widget with the parent hierarchy, filtered to the current tree."),
        why: t("Edit, sort and review many settings at once."),
        click: t("Click — the list opens; remove its chip to see all settings."),
        affects: t("Opens a separate list widget; the panel closes."),
        stored: t("Nothing is stored."),
    }),
    "widget.settings.legacy": (v, ctx) => ({
        title: t("Settings list (simple)"),
        what: t("A simple list of settings shown when the table view is not available; the pin marks the current one."),
        why: t("Switch settings even without the table."),
        click: t("Click — open in this widget (Alt+click — in a new widget); the copy icon — a new widget; the bin — archive (admins); drag — move in the tree (admins)."),
        affects: t("Archiving and moving change the tree for everyone."),
        stored: t("Archive and moves — in the database, immediately."),
    }),
    "widget.tabs.tab": (v, ctx) => ({
        title: t("Tab"),
        what: t("A section of this widget; its icon and hint say what is inside."),
        why: t("Splits a large form or report into parts."),
        click: t("Click — open the tab; some tabs load their data the first time you open them."),
        affects: t("Empty tabs are hidden while «Hide empty rows, columns, tabs» is on; «?» (Documentation) opens the section of the current tab."),
        stored: t("The open tab is kept while the widget is open and saved with the page layout (Commands → Page → Save)."),
        setting: "fastEntry.lazyTabs; fe.ux.lazyTabsDelayMs; fe.ux.lazyTabsChunk",
    }),
    "widget.tabs.count": (v, ctx) => ({
        title: t("Rows on the tab"),
        what: t("How many rows the table on this tab has (lines, for a text tab)."),
        why: t("See at a glance whether the tab has content."),
        affects: t("Nothing changes."),
        stored: t("Counted from the loaded data."),
    }),
    "widget.tabs.metricsBad": (v, ctx) => ({
        title: t("Bad metrics"),
        what: t("The red number on the Metrics tab: how many data rows have at least one metric graded Bad."),
        why: t("Signals problems in the data before you open the tab."),
        click: t("Click the tab — see which metrics and which rows."),
        affects: t("Nothing changes."),
        stored: t("Computed together with the data of this widget."),
        setting: "fe.ux.metricsTabBadBadge",
    }),
    "widget.tabs.flag": (v, ctx) => ({
        title: t("Tab flag"),
        what: t("The mark on a tab says its feature is on for this record (materialized view, calculation cascade)."),
        why: t("See important switches without opening the tab."),
        click: t("Click the tab — see and change the settings."),
        affects: t("Nothing changes."),
        stored: t("Comes with the record; the cascade state updates by itself."),
        states: { "flag": t("✓ — the feature is on (MV tab: «Create materialized view»)."), "on": t("White: takes part in a cascade because its sources are under it."), "use": t("Yellow: «Use in calculation cascade» is on."), "auto": t("Green: «Run automatically in the cascade» is on."), "dirty": t("Yellow-brown: dirty nodes in the sources — the data may be stale.") },
    }),
    // IE 2026 10 12 / ODOO-345618 (заказ Игоря: бейдж согласования на вкладке вместо чипса)
    "widget.tabs.approvalBadge": (v, ctx) => ({
        title: t("Approval progress"),
        what: t("The number on the Approval tab: approval units waiting for approval / all approval units of this report."),
        why: t("Shows how far the approval has gone without opening the tab; it replaces the Approval summary chip above the table."),
        click: t("Click the tab — the approval process in detail."),   // IE 2026 10 12 / ODOO-345618 (заказ Игоря: бейдж согласования на вкладке вместо чипса) (проба AB1): текст чипса — в строке Now
        affects: t("Nothing changes."),
        stored: t("Computed together with the data of this widget."),
        setting: "fe.ux.approvalTabBadge",
        states: { "normal": t("{chip}. Accent colour: nothing overdue and nothing returned to you."), "status": t("{chip}. Red: there are overdue units or units returned to you."),
            // IE 2026 10 13 / ODOO-5800 (заказ Игоря: бейдж — только по позициям отчёта): бейдж по строкам отчёта (бэк approval_badge_scope, флаг fe.approval.scopedBadge)
            "rowsNormal": t("{chip}. Counted only over the rows of this report: {rows} rows, {units} approval units. Accent colour: nothing overdue and nothing returned to you."),
            "rowsStatus": t("{chip}. Counted only over the rows of this report: {rows} rows, {units} approval units. Red: there are overdue units or units returned to you.") },   // IE 2026 10 12 / ODOO-345618 (заказ Игоря: бейдж согласования на вкладке вместо чипса) (проба AB1): {chip} — текст чипса сводки (vars.chip)
    }),
    "widget.mobile.close": (v, ctx) => ({
        title: t("Close widget"),
        what: t("Closes this widget."),
        why: t("Free the phone screen."),
        click: t("Tap — close."),
        affects: t("Another widget of the page becomes active."),
        stored: t("Only in this session."),
    }),
    "widget.mobile.tabs": (v, ctx) => ({
        title: t("Widget tabs"),
        what: t("Opens the list of this widget's tabs."),
        why: t("Tabs do not fit on a phone screen."),
        click: t("Tap — the list opens; tap a tab to switch."),
        affects: t("Only which tab is open."),
        stored: t("Nothing is stored."),
    }),
    "widget.mobile.vizExtras": (v, ctx) => ({
        title: t("Numbers, axes and legend"),
        what: t("For a table — shows row and column numbers; for a chart — its axes and legend."),
        why: t("They take space on a phone and are hidden by default."),
        click: t("Tap — show or hide."),
        affects: t("Only the view."),
        stored: t("Nothing is stored."),
        states: { "on": t("Shown."), "off": t("Hidden.") },
    }),
    "widget.mobile.chartSeries": (v, ctx) => ({
        title: t("Chart series"),
        what: t("Opens the list of chart series."),
        why: t("A readable legend with big rows on a phone."),
        click: t("Tap — open the list of series."),
        affects: t("Only the chart view."),
        stored: t("Nothing is stored."),
    }),
    "widget.mobile.gridPanel": (v, ctx) => ({
        title: t("Table commands"),
        what: t("Opens the command panel of the table (chips and commands) as a sheet."),
        why: t("On a phone the panel is hidden to leave room for rows."),
        click: t("Tap — open or close the sheet."),
        affects: t("Only the view."),
        stored: t("Nothing is stored."),
        states: { "on": t("The sheet is open."), "off": t("The sheet is closed.") },
    }),
    "widget.mobile.max": (v, ctx) => ({
        title: t("Maximize"),
        what: t("Spreads the widget over the whole phone screen, or restores its size."),
        why: t("Work with one widget without the others."),
        click: t("Tap — switch."),
        affects: t("Only the view."),
        stored: t("Not saved."),
        states: { "on": t("Maximized."), "off": t("Normal size.") },
    }),
    "widget.mobile.prev": (v, ctx) => ({
        title: t("Previous widget"),
        what: t("Moves to the previous widget of the page."),
        why: t("Browse widgets one by one on a phone."),
        click: t("Tap — go to it (on a maximized widget a swipe on the title does the same)."),
        affects: t("Only which widget is active."),
        stored: t("Nothing is stored."),
    }),
    "widget.mobile.next": (v, ctx) => ({
        title: t("Next widget"),
        what: t("Moves to the next widget of the page."),
        why: t("Browse widgets one by one on a phone."),
        click: t("Tap — go to it (on a maximized widget a swipe on the title does the same)."),
        affects: t("Only which widget is active."),
        stored: t("Nothing is stored."),
    }),
    "widget.mobile.cellMenu": (v, ctx) => ({
        title: t("Cell menu"),
        what: t("Opens the form of the selected cell."),
        why: t("On a phone the cell form has no hover arrow."),
        click: t("Tap — open or close the form."),
        affects: t("Only the view."),
        stored: t("Nothing is stored."),
    }),
    "widget.mobile.cellOpen": (v, ctx) => ({
        title: t("Open from cell"),
        what: t("Opens the record or document the selected cell refers to."),
        why: t("On a phone there is no Alt+click."),
        click: t("Tap — open it in a new widget."),
        affects: t("Adds a widget."),
        stored: t("Only in this session."),
    }),
    "widget.mobile.commands": (v, ctx) => ({
        title: t("Widget commands"),
        what: t("Opens the ⋯ form with the commands of this widget and its settings."),
        why: t("On a phone there is no hover over the corner."),
        click: t("Tap — open or close."),
        affects: t("Only the view."),
        stored: t("Nothing is stored."),
    }),
    "widget.mobile.tabsSheet": (v, ctx) => ({
        title: t("Tabs (phone)"),
        what: t("The tabs of this widget as a list."),
        why: t("Pick a tab on a narrow screen."),
        click: t("Tap a tab — open it; a tab with sub-tabs opens like a folder."),
        affects: t("Only which tab is open."),
        stored: t("Nothing is stored."),
    }),
    // IE 2026 09 27 / ODOO-… (заказ Игоря: картинки виджетов): пункт ≡ «Update preview»
    "widget.menu.previewUpdate": (v, ctx) => ({
        title: t("Update preview"),
        what: t("Takes a picture of what this widget shows now (without its header) and keeps it for the page previews."),
        why: t("The pages panel (view «Page previews») and the page menu (view «Widget previews») show the widgets by these pictures."),
        click: t("The interface pauses for a few seconds while the picture is taken."),
        affects: t("Everyone sees the same preview; the previous one is replaced."),
        stored: t("On the server, with the time, the author, the chips and the tab of the capture."),
        setting: "fe.ux.widgetPreviews",
    }),
}
export default widget
